// components/Careers.jsx
import React, { useState, useEffect } from 'react';
import '../assets/css/Careers.css';
import { supabase } from '../lib/supabase';

// Import career-related images
import CareerHeroImage from '../assets/picture_assets/hero1.jpg';
// import CultureImage from '../assets/picture_assets/hero1.jpg';
// import BenefitsImage from '../assets/picture_assets/hero1.jpg';

const Careers = ({ showJobOpenings = true }) => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [jobOpenings, setJobOpenings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [jobsPerPage] = useState(5);
  const [expandedJob, setExpandedJob] = useState(null);
  const [selectedTools, setSelectedTools] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  // Add state for "Others" input
  const [otherToolInput, setOtherToolInput] = useState('');
  const [showOtherToolInput, setShowOtherToolInput] = useState(false);
  const [otherSkillInput, setOtherSkillInput] = useState('');
  const [showOtherSkillInput, setShowOtherSkillInput] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [countryCode, setCountryCode] = useState('+63'); // Default to Philippines

  const categories = [
    { id: 'all', name: 'All Positions' },
    { id: 'virtual-assistant', name: 'Virtual Assistants' },
    { id: 'customer-service', name: 'Customer Service' },
    { id: 'marketing', name: 'Marketing' },
    { id: 'technology', name: 'Technology' }
  ];

  // Software tools options
  const softwareToolsOptions = [
    'Microsoft Office Suite',
    'Google Workspace',
    'Shopify',
    'WordPress',
    'Zoom',
    'Slack',
    'Trello',
    'Asana',
    'QuickBooks',
    'Xero',
    'Mailchimp',
    'Canva',
    'Adobe Creative Suite',
    'Salesforce',
    'HubSpot',
    'Zendesk',
    'Intercom',
    'Social Media Platforms',
    'CRM Software',
    'Project Management Tools'
  ];

  // Skills options
  const skillsOptions = [
    'Customer Service',
    'Data Entry',
    'Social Media Management',
    'Email Management',
    'Calendar Management',
    'Travel Planning',
    'Content Writing',
    'Graphic Design',
    'Video Editing',
    'SEO Optimization',
    'Digital Marketing',
    'Bookkeeping',
    'Inventory Management',
    'Order Processing',
    'Research Skills',
    'Transcription',
    'Translation',
    'Project Coordination',
    'Lead Generation',
    'Technical Support'
  ];

  // Sample data for fallback
  const sampleJobs = [
    {
      id: 1,
      title: "Virtual Assistant - E-commerce",
      category: "virtual-assistant",
      type: "Full-time",
      level: "Entry Level",
      location: "Remote",
      description: "Provide administrative support for e-commerce businesses including product listing, order processing, and customer service.",
      requirements: "1+ years experience in e-commerce or related field, Excellent communication skills, Proficiency in Shopify/Amazon/WooCommerce, Strong organizational skills, Attention to detail",
      benefits: "Competitive salary, Health insurance, Flexible schedule, Career growth opportunities, Performance bonuses, Remote work setup allowance",
      responsibilities: "Manage product listings, Process orders, Handle customer inquiries, Update inventory, Generate sales reports",
      timestamp: new Date(),
      active: true
    },
    {
      id: 2,
      title: "Executive Virtual Assistant",
      category: "virtual-assistant",
      type: "Full-time",
      level: "Mid Level",
      location: "Remote",
      description: "Provide high-level administrative support to executives including calendar management, travel arrangements, and communication handling.",
      requirements: "3+ years experience as an executive assistant, Exceptional organizational skills, Discretion and confidentiality, Proficiency in office software, Excellent written and verbal communication",
      benefits: "Competitive salary, Health insurance, Professional development allowance, Performance bonuses, Flexible working hours, Wellness program",
      responsibilities: "Calendar management, Travel arrangements, Email management, Document preparation, Meeting coordination, Report generation",
      timestamp: new Date(),
      active: true
    }
  ];

  // Fetch jobs from Supabase
  const fetchJobs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data: jobsData, error } = await supabase
        .from('job_openings')
        .select('*')
        .eq('active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setJobOpenings(jobsData || []);
      
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError(`Note: Could not load live job data. Showing sample positions. (${err.message})`);
      setJobOpenings(sampleJobs);
    } finally {
      setLoading(false);
    }
  };

  // Handle tool selection
  const handleToolSelection = (tool) => {
    if (tool === 'Others') {
      setShowOtherToolInput(true);
      return;
    }
    setSelectedTools(prev => {
      if (prev.includes(tool)) {
        return prev.filter(t => t !== tool);
      } else {
        return [...prev, tool];
      }
    });
  };

  // Handle skill selection
  const handleSkillSelection = (skill) => {
    if (skill === 'Others') {
      setShowOtherSkillInput(true);
      return;
    }
    setSelectedSkills(prev => {
      if (prev.includes(skill)) {
        return prev.filter(s => s !== skill);
      } else {
        return [...prev, skill];
      }
    });
  };

  // Handle adding "Other" tool
  const handleOtherToolAdd = (e) => {
    if (
      (e.type === 'blur' || (e.type === 'keydown' && e.key === 'Enter')) &&
      otherToolInput.trim() !== ''
    ) {
      setSelectedTools(prev => [...prev, otherToolInput.trim()]);
      setOtherToolInput('');
      setShowOtherToolInput(false);
    }
  };

  // Handle adding "Other" skill
  const handleOtherSkillAdd = (e) => {
    if (
      (e.type === 'blur' || (e.type === 'keydown' && e.key === 'Enter')) &&
      otherSkillInput.trim() !== ''
    ) {
      setSelectedSkills(prev => [...prev, otherSkillInput.trim()]);
      setOtherSkillInput('');
      setShowOtherSkillInput(false);
    }
  };

  // Application submission handler
  const handleApplicationSubmit = async (e) => {
    e.preventDefault();

    if (!agreeToTerms) {
      alert('Please agree to the terms and conditions before submitting your application.');
      return;
    }

    const formData = new FormData(e.target);
    let phone = formData.get('phone');
    // Only append if phone is not empty
    if (phone && phone.trim() !== '') {
      phone = `${countryCode}${phone.replace(/^0+/, '')}`;
    }

    const applicationData = {
      position: selectedPosition.title,
      first_name: formData.get('firstName'),
      last_name: formData.get('lastName'),
      email: formData.get('email'),
      phone: phone,
      cover_letter: formData.get('coverLetter'),
      work_availability: formData.get('workAvailability'),
      software_tools: selectedTools.join(', '),
      skills: selectedSkills.join(', '),
      status: 'new',
      application_date: new Date().toISOString()
    };

    try {
      const { error } = await supabase
        .from('applications')
        .insert([applicationData])
        .select();

      if (error) throw error;
      
      alert('Application submitted successfully!');
      closeApplicationModal();
      e.target.reset();
      // Reset selections
      setSelectedTools([]);
      setSelectedSkills([]);
      setAgreeToTerms(false);
    } catch (error) {
      console.error('Error submitting application:', error);
      alert('There was an error submitting your application. Please try again.');
    }
  };

  // Fetch jobs on component mount
  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter jobs by category
  const filteredJobs = activeCategory === 'all' 
    ? jobOpenings 
    : jobOpenings.filter(job => job.category === activeCategory);

  // Get current jobs for pagination
  const indexOfLastJob = currentPage * jobsPerPage;
  const indexOfFirstJob = indexOfLastJob - jobsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);
  const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const openApplicationModal = (position) => {
    setSelectedPosition(position);
    setShowApplicationModal(true);
  };

  const closeApplicationModal = () => {
    setShowApplicationModal(false);
    setSelectedPosition(null);
    // Reset selections when modal closes
    setSelectedTools([]);
    setSelectedSkills([]);
    setAgreeToTerms(false);
  };

  const viewJobDetails = (job) => {
    setExpandedJob(job);
  };

  const closeJobDetails = () => {
    setExpandedJob(null);
  };

  // Retry fetching jobs
  const retryFetchJobs = () => {
    fetchJobs();
  };

  // NEW: Helper function to format job content with bullet points
 // ENHANCED: Helper function to format job content with proper bullet points
const formatJobContent = (content) => {
  if (!content) return null;
  
  // First, split by manual line breaks
  const lines = content.split('\n').filter(line => line.trim().length > 0);
  
  let allItems = [];
  
  lines.forEach(line => {
    const trimmedLine = line.trim();
    
    // Remove any bullet points, dashes, or numbers from the beginning
    const cleanLine = trimmedLine.replace(/^[•-]\s*/, '').replace(/^\d+\.\s*/, '');
    
    // If the line already ends with a period or looks like a complete sentence, use as-is
    if (cleanLine.match(/[.!?]$/) || cleanLine.length < 100) {
      allItems.push(cleanLine.replace(/[.!?]+$/, ''));
    } else {
      // Otherwise, try to split by sentences within the line
      const sentences = cleanLine.split(/(?<=[.!?])\s+/);
      sentences.forEach(sentence => {
        const cleanSentence = sentence.trim().replace(/[.!?]+$/, '');
        if (cleanSentence.length > 0) {
          allItems.push(cleanSentence);
        }
      });
    }
  });
  
  // If we found multiple items, render as bullet points
  if (allItems.length > 1) {
    return (
      <ul className="job-content-list">
        {allItems.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
    );
  }
  
  // If only one item or no proper splitting occurred, return as paragraph
  return <p>{content}</p>;
};

  // Render pagination controls
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="pagination">
        <button 
          onClick={() => paginate(currentPage - 1)} 
          disabled={currentPage === 1}
          className="pagination-btn"
        >
          &laquo; Previous
        </button>
        
        <div className="page-numbers">
          {pageNumbers.map(number => (
            <button
              key={number}
              onClick={() => paginate(number)}
              className={currentPage === number ? 'active' : ''}
            >
              {number}
            </button>
          ))}
        </div>
        
        <button 
          onClick={() => paginate(currentPage + 1)} 
          disabled={currentPage === totalPages}
          className="pagination-btn"
        >
          Next &raquo;
        </button>
      </div>
    );
  };

  // If job details view is open, show that instead
  if (expandedJob) {
    return (
      <div className="job-detail-view">
        <div className="container">
          <button onClick={closeJobDetails} className="back-button">
            &larr; Back to Jobs
          </button>
          
          <div className="job-detail-card">
            <h1>{expandedJob.title}</h1>
            
            <div className="job-meta">
              <span className="job-type">{expandedJob.type}</span>
              <span className="job-level">{expandedJob.level}</span>
              <span className="job-location">{expandedJob.location}</span>
            </div>
            
            <div className="job-section">
              <h2>Job Description</h2>
              <p>{expandedJob.description}</p>
            </div>
            
            {expandedJob.responsibilities && (
              <div className="job-section">
                <h2>Key Responsibilities</h2>
                {formatJobContent(expandedJob.responsibilities)}
              </div>
            )}
            
            {expandedJob.requirements && (
              <div className="job-section">
                <h2>Requirements</h2>
                {formatJobContent(expandedJob.requirements)}
              </div>
            )}
            
            {expandedJob.benefits && (
              <div className="job-section">
                <h2>Benefits</h2>
                {formatJobContent(expandedJob.benefits)}
              </div>
            )}
            
            <button 
              onClick={() => openApplicationModal(expandedJob)}
              className="btn-primary apply-detail-btn"
            >
              Apply Now
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section id="careers" className="careers">
      {/* Hero Section */}
      <div className="careers-hero">
        <div className="careers-hero-content">
          <h1>Build Your Career with BPD Online Corp</h1>
          <p>Join our team of talented professionals and help us deliver exceptional virtual assistant services to clients worldwide.</p>
          {!showJobOpenings && (
            <div className="view-all-jobs-link">
              <a href="/careers" className="btn-primary">View All Job Openings</a>
            </div>
          )}
        </div>
        <div className="careers-hero-image">
          <img src={CareerHeroImage} alt="BPD Online Corp Team" />
        </div>
      </div>

      {/* Conditionally render Open Positions Section based on prop */}
      {showJobOpenings && (
        <div className="open-positions">
          <div className="container">
            <div className="section-header">
              <h2>Current Openings</h2>
              <div className="header-divider"></div>
              <p>Explore our available positions and find where you fit in our growing team.</p>
            </div>

            {loading && <div className="loading">Loading job openings...</div>}
            
            {error && (
              <div className="error-message">
                <p>{error}</p>
                <button onClick={retryFetchJobs} className="btn-outline">
                  Try Again
                </button>
              </div>
            )}

            {/* Category Filters */}
            <div className="category-filters">
              {categories.map(category => (
                <button
                  key={category.id}
                  className={`filter-btn ${activeCategory === category.id ? 'active' : ''}`}
                  onClick={() => {
                    setActiveCategory(category.id);
                    setCurrentPage(1);
                  }}
                >
                  {category.name}
                </button>
              ))}
            </div>

            {/* Jobs List */}
            <div className="jobs-list">
              {!loading && !error && currentJobs.length > 0 ? (
                currentJobs.map(job => (
                  <div key={job.id} className="job-card">
                    <div className="job-main-info">
                      <h3>{job.title}</h3>
                      <div className="job-meta">
                        <span className="job-type">{job.type}</span>
                        <span className="job-level">{job.level}</span>
                        <span className="job-location">{job.location}</span>
                      </div>
                      <p className="job-description">{job.description}</p>
                    </div>
                    
                    <div className="job-actions">
                      <button 
                        className="btn-outline"
                        onClick={() => openApplicationModal(job)}
                      >
                        Apply Now
                      </button>
                      <button 
                        className="btn-text"
                        onClick={() => viewJobDetails(job)}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                !loading && !error && (
                  <div className="no-jobs">
                    <p>No positions available at the moment. Please check back later!</p>
                  </div>
                )
              )}
            </div>

            {/* Pagination Controls */}
            {!loading && !error && renderPagination()}

            {/* Results count */}
            {!loading && !error && (
              <div className="results-count">
                Showing {indexOfFirstJob + 1}-{Math.min(indexOfLastJob, filteredJobs.length)} of {filteredJobs.length} positions
              </div>
            )}
          </div>
        </div>
      )}

      {/* Application Modal */}
      {showApplicationModal && (
        <div className="modal-overlay">
          <div className="modal-content application-modal">
            <div className="modal-header">
              <h2>Apply for {selectedPosition.title}</h2>
              <button className="close-button" onClick={closeApplicationModal}>×</button>
            </div>
            <div className="modal-body">
              <form className="application-form" onSubmit={handleApplicationSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name *</label>
                    <input type="text" id="firstName" name="firstName" required />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name *</label>
                    <input type="text" id="lastName" name="lastName" required />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input type="email" id="email" name="email" required />
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <div className="phone-input-row">
                    <select
                      name="countryCode"
                      value={countryCode}
                      onChange={e => setCountryCode(e.target.value)}
                      className="phone-country-select"
                      style={{
                        width: '90px',
                        minWidth: '70px',
                        maxWidth: '110px',
                        padding: '0.3rem 0.5rem',
                        fontSize: '0.98rem',
                        height: '36px',
                        background: '#f8fafc',
                        border: '1px solid #d1d5db',
                        borderRadius: '5px'
                      }}
                    >
                      <option value="+63">🇵🇭 +63</option>
                      <option value="+1">🇺🇸 +1</option>
                      <option value="+44">🇬🇧 +44</option>
                      <option value="+61">🇦🇺 +61</option>
                      <option value="+91">🇮🇳 +91</option>
                      <option value="+81">🇯🇵 +81</option>
                      <option value="+65">🇸🇬 +65</option>
                      <option value="+971">🇦🇪 +971</option>
                      <option value="+49">🇩🇪 +49</option>
                      <option value="+33">🇫🇷 +33</option>
                    </select>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      placeholder="9XXXXXXXXX"
                      pattern="[0-9]{7,15}"
                      className="phone-number-field"
                      style={{
                        flex: 1,
                        minWidth: 0,
                        padding: '0.7rem 0.9rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '5px',
                        fontSize: '1rem',
                        background: '#fff',
                        height: '36px'
                      }}
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="workAvailability">Work Availability *</label>
                  <select id="workAvailability" name="workAvailability" required>
                    <option value="">Select availability</option>
                    <option value="immediately">Immediately</option>
                    <option value="1_week">Within 1 week</option>
                    <option value="2_weeks">Within 2 weeks</option>
                    <option value="1_month">Within 1 month</option>
                    <option value="negotiable">Negotiable</option>
                  </select>
                </div>
                
                {/* Software Tools Multi-select Dropdown */}
                <div className="form-group">
                  <label>Software Tools & Platforms You're Proficient With *</label>
                  <div className="multi-select-dropdown">
                    <div className="selected-options">
                      {selectedTools.map(tool => (
                        <span key={tool} className="selected-tag">
                          {tool}
                          <button 
                            type="button"
                            onClick={() => setSelectedTools(prev => prev.filter(t => t !== tool))}
                            className="remove-tag"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="dropdown-options">
                      {softwareToolsOptions.map(tool => (
                        <label key={tool} className="dropdown-option">
                          <input
                            type="checkbox"
                            checked={selectedTools.includes(tool)}
                            onChange={() => handleToolSelection(tool)}
                          />
                          {tool}
                        </label>
                      ))}
                      <label key="Others" className="dropdown-option">
                        <input
                          type="checkbox"
                          checked={showOtherToolInput}
                          onChange={() => setShowOtherToolInput(v => !v)}
                        />
                        Others
                      </label>
                      {showOtherToolInput && (
                        <input
                          type="text"
                          className="other-input"
                          placeholder="Please specify"
                          value={otherToolInput}
                          onChange={e => setOtherToolInput(e.target.value)}
                          onBlur={handleOtherToolAdd}
                          onKeyDown={handleOtherToolAdd}
                          autoFocus
                        />
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Skills Multi-select Dropdown */}
                <div className="form-group">
                  <label>Key Skills & Expertise *</label>
                  <div className="multi-select-dropdown">
                    <div className="selected-options">
                      {selectedSkills.map(skill => (
                        <span key={skill} className="selected-tag">
                          {skill}
                          <button 
                            type="button"
                            onClick={() => setSelectedSkills(prev => prev.filter(s => s !== skill))}
                            className="remove-tag"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="dropdown-options">
                      {skillsOptions.map(skill => (
                        <label key={skill} className="dropdown-option">
                          <input
                            type="checkbox"
                            checked={selectedSkills.includes(skill)}
                            onChange={() => handleSkillSelection(skill)}
                          />
                          {skill}
                        </label>
                      ))}
                      <label key="Others" className="dropdown-option">
                        <input
                          type="checkbox"
                          checked={showOtherSkillInput}
                          onChange={() => setShowOtherSkillInput(v => !v)}
                        />
                        Others
                      </label>
                      {showOtherSkillInput && (
                        <input
                          type="text"
                          className="other-input"
                          placeholder="Please specify"
                          value={otherSkillInput}
                          onChange={e => setOtherSkillInput(e.target.value)}
                          onBlur={handleOtherSkillAdd}
                          onKeyDown={handleOtherSkillAdd}
                          autoFocus
                        />
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="coverLetter">google drive link*</label>
                  <textarea 
                    id="coverLetter" 
                    name="coverLetter"
                    rows="5" 
                    placeholder="Please provide a link to your current resume/CV on Google Drive or similar platform."
                    required
                  ></textarea>
                </div>
                
                {/* Terms and Conditions */}
                <div className="form-group terms-section">
                  <label className="terms-label">
                    <input
                      type="checkbox"
                      checked={agreeToTerms}
                      onChange={(e) => setAgreeToTerms(e.target.checked)}
                      required
                    />
                    <span>
                      I agree to the{' '}
                      <button
                        type="button"
                        className="link-btn"
                        onClick={() => setShowTermsModal(true)}
                      >
                        Terms and Conditions
                      </button>
                      {' '}and{' '}
                      <button
                        type="button"
                        className="link-btn"
                        onClick={() => setShowPrivacyModal(true)}
                      >
                        Privacy Policy
                      </button>
                      {' '}*
                    </span>
                  </label>
                  <p className="terms-note">
                    By submitting this application, you consent to the processing of your personal data 
                    in accordance with our privacy policy and agree to be contacted regarding your application.
                  </p>
                </div>
                
                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={closeApplicationModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={!agreeToTerms}>
                    Submit Application
                  </button>
                </div>
              </form>
            </div>
          </div>
          {/* Terms Modal */}
          {showTermsModal && (
            <div className="modal-overlay">
              <div className="modal-content" style={{maxWidth: 500, maxHeight: '80vh', overflowY: 'auto'}}>
                <div className="modal-header">
                  <h2>Terms and Conditions</h2>
                  <button className="close-button" onClick={() => setShowTermsModal(false)}>×</button>
                </div>
                <div className="modal-body">
                  {/* Replace below with your actual terms */}
                  <p>
                    These are the sample Terms and Conditions. By applying, you agree to provide accurate information and consent to the processing of your data for recruitment purposes. Your application does not guarantee employment. For full details, contact our HR department.
                  </p>
                </div>
              </div>
            </div>
          )}
          {/* Privacy Policy Modal */}
          {showPrivacyModal && (
            <div className="modal-overlay">
              <div className="modal-content" style={{maxWidth: 500, maxHeight: '80vh', overflowY: 'auto'}}>
                <div className="modal-header">
                  <h2>Privacy Policy</h2>
                  <button className="close-button" onClick={() => setShowPrivacyModal(false)}>×</button>
                </div>
                <div className="modal-body">
                  {/* Replace below with your actual privacy policy */}
                  <p>
                    We respect your privacy. Your personal data will be used solely for the purpose of processing your job application and will not be shared with third parties except as required by law. You may request deletion of your data at any time.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default Careers;